import React, { useState, useCallback, memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { trackNodeInteraction, trackExternalLink, trackSpotifyInteraction } from '../utils/analytics';

export interface RoadmapNodeData {
  label: string;
  description?: string;
  videoLink?: string;
  spotifyEmbed?: string;
  tips?: string[];
  color?: string;
  gradient?: string;
  isMain?: boolean;
  isChild?: boolean;
  isSubChild?: boolean;
  hasChildren?: boolean;
  childrenCount?: number;
  sectionId?: string;
  isBackButton?: boolean;
  isNextStepButton?: boolean;
  nextSectionId?: string;
  initiallyExpanded?: boolean;
}

const RoadmapNode: React.FC<NodeProps<RoadmapNodeData>> = ({ data, id }) => {
  const [isExpanded, setIsExpanded] = useState(data.initiallyExpanded || false);
  
  const toggleExpand = useCallback(() => {
    const newExpandState = !isExpanded;
    setIsExpanded(newExpandState);
    
    // Track node expand/collapse
    trackNodeInteraction(
      id, 
      data.label, 
      newExpandState ? 'expand' : 'collapse'
    );
  }, [isExpanded, id, data.label]);
  
  // Handle video link click while preventing event propagation
  const handleVideoLinkClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the node toggle event from firing
    
    if (data.videoLink) {
      try {
        // Track video link click
        trackExternalLink(
          'https://www.univeli.com/link/4BP8Zb?url=https%3A%2F%2Fwww.univeli.com%2Fcourse%2Fprepare-me-for-high-tech',
          'video_tutorial'
        );
        window.open('https://www.univeli.com/link/4BP8Zb?url=https%3A%2F%2Fwww.univeli.com%2Fcourse%2Fprepare-me-for-high-tech', '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open video link:', error);
      }
    }
  }, [data.videoLink]);

  // Handle Spotify interaction
  const handleSpotifyInteraction = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the node toggle event from firing
    // Track Spotify play action
    trackSpotifyInteraction('play');
  }, []);
  
  // Determine if this node should have handles
  const showSourceHandle = !data.isBackButton;
  const showTargetHandle = !data.isBackButton;
  
  // Define styles based on node type
  let nodeStyle: React.CSSProperties = {
    padding: '15px',
    borderRadius: '12px',
    width: data.spotifyEmbed ? '320px' : data.isBackButton ? '160px' : '220px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'right',
    direction: 'rtl',
    border: '1px solid rgba(0,0,0,0.1)',
  };
  
  if (data.gradient) {
    nodeStyle = {
      ...nodeStyle,
      background: data.gradient,
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
    };
  } else if (data.color) {
    nodeStyle = {
      ...nodeStyle,
      backgroundColor: data.color,
    };
  }
  
  // Special styling for back button
  if (data.isBackButton) {
    nodeStyle = {
      ...nodeStyle,
      backgroundColor: '#f8f9fa',
      border: '2px solid #1a73e8',
      color: '#1a73e8',
      textAlign: 'center',
      fontWeight: 'bold',
      textShadow: 'none',
      boxShadow: '0 2px 8px rgba(26, 115, 232, 0.2)',
    };
  }
  
  // Memoized button style handlers
  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#0056b3';
  }, []);
  
  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#007bff';
  }, []);
  
  return (
    <div 
      style={nodeStyle} 
      onClick={toggleExpand}
      role="button"
      aria-expanded={isExpanded}
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleExpand();
        }
      }}
    >
      {showTargetHandle && <Handle type="target" position={Position.Top} />}
      
      <div style={{ 
        fontWeight: 'bold', 
        fontSize: data.isMain ? '18px' : '16px', 
        marginBottom: isExpanded ? '10px' : '0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {data.label}
        
        {!data.isBackButton && data.hasChildren && (
          <span 
            style={{ 
              fontSize: '12px', 
              color: data.gradient ? '#fff' : '#666',
              backgroundColor: data.gradient ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            role="img"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? '▼' : '►'}
          </span>
        )}
      </div>
      
      {isExpanded && (
        <>
          {data.description && (
            <div style={{ 
              fontSize: '14px', 
              marginTop: '10px', 
              padding: '8px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '6px'
            }}>
              {data.description}
              {data.videoLink && (
                <button
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#007bff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleVideoLinkClick}
                  aria-label="לחץ כאן להסבר נוסף"
                >
                  לחץ כאן להסבר נוסף
                </button>
              )}
            </div>
          )}
          
          {data.spotifyEmbed && (
            <div 
              style={{ 
                marginTop: '15px',
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'center',
              }}
              onClick={handleSpotifyInteraction}
            >
              <iframe
                src={data.spotifyEmbed}
                frameBorder="0"
                allowTransparency={true}
                allow="encrypted-media"
                width="100%"
                height="152"
                title="Spotify Embed"
              />
            </div>
          )}
          
          {data.tips && data.tips.length > 0 && (
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '14px', marginTop: '10px' }}>טיפים:</div>
              <ul style={{ paddingRight: '20px', paddingLeft: '0', fontSize: '12px', marginRight: '0' }}>
                {data.tips.map((tip, index) => (
                  <li key={`tip-${index}`}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      
      {showSourceHandle && <Handle type="source" position={Position.Bottom} />}
    </div>
  );
};

export default memo(RoadmapNode);